/**
 * Concierge CSS Generator
 * Generates CSS directly from bc-widgets theme data.
 * This is the only theming system for Concierge - themes are managed
 * through the bc-widgets theme editor.
 */

// ============================================================================
// TYPES
// ============================================================================

/**
 * Font value from bc-widgets theme config
 * Mirrors ThemeFontValue from @brandclub/bc-widgets
 */
export interface WidgetThemeFontValue {
  name: string;
  url: string;
  format: string; // 'google' | 'woff2' | 'woff' | 'ttf' | 'otf' | 'system'
  weights?: { weight: number; url: string }[];
}

/**
 * bc-widgets theme data structure returned by useWidgetTheme
 */
export interface WidgetThemeData {
  config?: {
    colors?: Record<string, string>;
    fonts?: Record<string, WidgetThemeFontValue>;
    icons?: Record<string, { name: string; url: string }>;
    widgetConfig?: Record<string, Record<string, unknown>>;
  };
  themeName?: string;
  appThemeId?: string;
}

/**
 * Internal font face definition for @font-face CSS generation
 */
interface FontFace {
  family: string;
  src: string;
  weight?: string | number;
  style?: 'normal' | 'italic';
}

// ============================================================================
// DEFAULTS
// ============================================================================

/**
 * Default values used when widget theme data is incomplete.
 * These ensure Concierge always has some baseline styling.
 */
const DEFAULTS = {
  fontFallback: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  colors: {
    primary: 'oklch(0.3269 0.1019 273.44)',
    primaryForeground: '#ffffff',
    background: '#ffffff',
    foreground: '#1a1a1a',
    muted: '#6b7280',
    border: '#e5e5e5',
  },
  borderRadius: '0.5rem',
};

// ============================================================================
// KEY MAPPINGS
// ============================================================================

/**
 * Color key mappings from bc-widgets to CSS variable names
 * Supports both camelCase and kebab-case keys from widget config
 */
const COLOR_KEY_MAP: Record<string, string> = {
  primary: 'primary',
  primaryForeground: 'primaryForeground',
  'primary-foreground': 'primaryForeground',
  background: 'background',
  foreground: 'foreground',
  muted: 'muted',
  mutedForeground: 'muted',
  'muted-foreground': 'muted',
  border: 'border',
};

/**
 * Font role mappings from bc-widgets to font family assignments
 */
const FONT_KEY_MAP: Record<string, string> = {
  body: 'body',
  heading: 'heading',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
};

/**
 * Theme key the server assigns to a Concierge-Studio-uploaded brand font
 * (see BrandAppWidgetThemeService.mapStudioConfigToThemeConfig). When present
 * it is the brand's chosen Concierge font and takes priority over `body`.
 */
const STUDIO_FONT_KEY = 'studio-brand-font';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Generate @font-face declaration from a FontFace config
 */
function generateFontFace(face: FontFace): string {
  const format = face.src.endsWith('.woff2')
    ? 'woff2'
    : face.src.endsWith('.woff')
    ? 'woff'
    : face.src.endsWith('.ttf')
    ? 'truetype'
    : face.src.endsWith('.otf')
    ? 'opentype'
    : 'woff2';

  return `@font-face {
  font-family: "${face.family}";
  src: url("${face.src}") format("${format}");
  font-weight: ${face.weight || 400};
  font-style: ${face.style || 'normal'};
  font-display: swap;
}`;
}

// ============================================================================
// MAIN FUNCTION
// ============================================================================

/**
 * Generate CSS for Concierge directly from bc-widgets theme data.
 *
 * This is the primary and only theming system for Concierge.
 * Themes are configured in the bc-widgets theme editor and this function
 * converts that data directly into CSS variables and font rules.
 *
 * @param themeData - Theme data from bc-widgets useWidgetTheme hook
 * @returns CSS string to inject into Shadow DOM
 *
 * @example
 * const { data: widgetTheme } = useWidgetTheme();
 * const themeCSS = generateConciergeCSSFromWidgetTheme(widgetTheme);
 */
export function generateConciergeCSSFromWidgetTheme(themeData: WidgetThemeData | undefined | null): string {
  // Extract widget theme config
  const widgetColors = themeData?.config?.colors;
  const widgetFonts = themeData?.config?.fonts;
  const widgetConfig = themeData?.config?.widgetConfig;

  // ========================================
  // CONVERT COLORS
  // ========================================
  const colors: Record<string, string> = {};
  if (widgetColors && Object.keys(widgetColors).length > 0) {
    for (const [key, value] of Object.entries(widgetColors)) {
      const mappedKey = COLOR_KEY_MAP[key];
      if (mappedKey && value) {
        colors[mappedKey] = value;
      }
    }
  }

  // ========================================
  // CONVERT FONTS
  // ========================================
  const fontFamilies: Record<string, string> = {};
  const googleFontUrls: string[] = [];
  const fontFaces: FontFace[] = [];

  if (widgetFonts && Object.keys(widgetFonts).length > 0) {
    for (const [key, fontValue] of Object.entries(widgetFonts)) {
      if (!fontValue) continue;

      const { name, url, format, weights } = fontValue;

      // Map to font family names for specific roles
      const mappedKey = FONT_KEY_MAP[key];
      if (mappedKey) {
        fontFamilies[mappedKey] = name;
      }
      if (key === STUDIO_FONT_KEY) {
        fontFamilies.studio = name;
      }

      // Collect font URLs for loading
      if (format === 'google' && url) {
        if (!googleFontUrls.includes(url)) {
          googleFontUrls.push(url);
        }
      } else if (weights?.length) {
        // Multi-weight font (e.g., from Concierge Studio) — one @font-face per weight
        for (const w of weights) {
          fontFaces.push({
            family: name,
            src: w.url,
            weight: w.weight,
            style: 'normal',
          });
        }
      } else if (format && format !== 'system' && url) {
        fontFaces.push({
          family: name,
          src: url,
          weight: 400,
          style: 'normal',
        });
      }
    }
  }

  // ========================================
  // EXTRACT TYPOGRAPHY FROM WIDGET CONFIG
  // ========================================
  const conciergeWidgetConfig = widgetConfig?.['concierge-assistant-message'];
  const typography: Record<string, string> = {};

  if (conciergeWidgetConfig?.fontSize) {
    typography.body = `${conciergeWidgetConfig.fontSize}px`;
  }

  const borderRadius = conciergeWidgetConfig?.borderRadius ? `${conciergeWidgetConfig.borderRadius}px` : undefined;

  // ========================================
  // GENERATE CSS
  // ========================================

  // Generate @font-face declarations for custom uploaded fonts
  const fontFacesCSS = fontFaces.map(generateFontFace).join('\n\n') || '';

  // Google Fonts @import
  const fontImport = googleFontUrls.length > 0 ? `@import url('${googleFontUrls[0]}');` : '';

  // Combine font declarations
  const fontDeclarations = [fontFacesCSS, fontImport].filter(Boolean).join('\n\n');
  const fontSection = fontDeclarations ? `${fontDeclarations}\n\n` : '';

  // Font families with cascading: studio-uploaded brand font > body > defaults.
  // The studio font is the brand's explicit Concierge choice, so every widget
  // inherits it; `body` only applies when no studio font was uploaded.
  const bodyFamily = fontFamilies.studio || fontFamilies.body;
  const bodyFont = bodyFamily ? `"${bodyFamily}", ${DEFAULTS.fontFallback}` : DEFAULTS.fontFallback;
  const headingFont = fontFamilies.heading ? `"${fontFamilies.heading}", Georgia, serif` : null;

  // Apply colors with defaults
  const c = {
    primary: colors.primary || DEFAULTS.colors.primary,
    primaryForeground: colors.primaryForeground || DEFAULTS.colors.primaryForeground,
    background: colors.background || DEFAULTS.colors.background,
    foreground: colors.foreground || DEFAULTS.colors.foreground,
    muted: colors.muted || DEFAULTS.colors.muted,
    border: colors.border || DEFAULTS.colors.border,
  };

  const radius = borderRadius || DEFAULTS.borderRadius;

  // Typography sizes with defaults
  const t = {
    h1: typography.h1 || '28px',
    h2: typography.h2 || '24px',
    h3: typography.h3 || '20px',
    body: typography.body || '16px',
  };

  // Build font CSS rules with cascading support
  const fontRules: string[] = [];

  // Body font applies to the container - children inherit via CSS inheritance
  // We intentionally do NOT use #concierge-mount * because that would override
  // CSS inheritance and prevent inline styles on parent elements from cascading to children
  fontRules.push(`/* Body font for container - children inherit naturally */
#concierge-mount,
.concierge-root {
  font-family: ${bodyFont} !important;
}`);

  // Heading font applies to all h1-h6 (if set)
  if (headingFont) {
    fontRules.push(`/* Heading font for h1-h6 */
#concierge-mount h1,
#concierge-mount h2,
#concierge-mount h3,
#concierge-mount h4,
#concierge-mount h5,
#concierge-mount h6 {
  font-family: ${headingFont} !important;
}`);
  }

  // Specific tag overrides (h1, h2, h3, etc.)
  const tagOverrides: Array<{ tag: string; fontName: string | undefined }> = [
    { tag: 'h1', fontName: fontFamilies.h1 },
    { tag: 'h2', fontName: fontFamilies.h2 },
    { tag: 'h3', fontName: fontFamilies.h3 },
    { tag: 'h4', fontName: fontFamilies.h4 },
    { tag: 'h5', fontName: fontFamilies.h5 },
    { tag: 'h6', fontName: fontFamilies.h6 },
  ];

  for (const { tag, fontName } of tagOverrides) {
    if (fontName) {
      const tagFont = `"${fontName}", Georgia, serif`;
      fontRules.push(`/* Specific font for ${tag} */
#concierge-mount ${tag} {
  font-family: ${tagFont} !important;
}`);
    }
  }

  return `${fontSection}/* Concierge Theme */
:host,
.concierge-root {
  /* Colors - set both --color-* and --tw-color-* for Tailwind v4 compatibility */
  --color-primary: ${c.primary} !important;
  --tw-color-primary: ${c.primary} !important;
  --color-primary-foreground: ${c.primaryForeground} !important;
  --tw-color-primary-foreground: ${c.primaryForeground} !important;
  --color-background: ${c.background} !important;
  --tw-color-background: ${c.background} !important;
  --color-foreground: ${c.foreground} !important;
  --tw-color-foreground: ${c.foreground} !important;
  --color-muted-foreground: ${c.muted} !important;
  --tw-color-muted-foreground: ${c.muted} !important;
  --color-border: ${c.border} !important;
  --tw-color-border: ${c.border} !important;
  --color-input: ${c.border} !important;
  --tw-color-input: ${c.border} !important;
  --radius: ${radius} !important;

  /* Typography sizes */
  --font-size-h1: ${t.h1} !important;
  --font-size-h2: ${t.h2} !important;
  --font-size-h3: ${t.h3} !important;
  --font-size-body: ${t.body} !important;
  --font-size-small: 14px !important;
}

${fontRules.join('\n\n')}`;
}

/**
 * Check if widget theme data is loaded and has content
 */
export function hasThemeData(themeData: WidgetThemeData | undefined | null): boolean {
  if (!themeData?.config) return false;

  const { colors, fonts } = themeData.config;
  const hasColors: boolean = colors != null && Object.keys(colors).length > 0;
  const hasFonts: boolean = fonts != null && Object.keys(fonts).length > 0;

  return hasColors || hasFonts;
}
